import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';
import type { Language } from '@/localization/types';
import { DEFAULT_LANGUAGE, isLanguageCode } from '@/localization/languages';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  organizationDefault?: Language;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  organizationDefault?: Language;
}

export function LanguageProvider({ children, organizationDefault }: LanguageProviderProps) {

  const [language, setLanguageState] = useState<Language>(() => {
    // Check for user preferences first
    const cookieLang = Cookies.get('language');
    const storageLang = localStorage.getItem('language');
    
    // If user has a preference, use it
    if (cookieLang && isLanguageCode(cookieLang)) {
      return cookieLang;
    }

    if (storageLang && isLanguageCode(storageLang)) {
      return storageLang;
    }
    
    // If organizationDefault is available during init, use it
    if (organizationDefault && isLanguageCode(organizationDefault)) {
      return organizationDefault;
    }
    
    // Otherwise, start with default language but this will be updated when organizationDefault loads
    return DEFAULT_LANGUAGE;
  });

  const syncLanguageStorage = (lang: Language) => {
    try {
      Cookies.set('language', lang, { expires: 365 });
    } catch (error) {
      console.error('Language cookie write failed, using localStorage fallback:', error);
    }
    localStorage.setItem('language', lang);
  };

  const syncI18nLanguage = (lang: Language) => {
    if (typeof window !== 'undefined' && (window as any).i18n) {
      (window as any).i18n.changeLanguage(lang);
    }
  };

  // React to organizationDefault changes when no user preference exists
  useEffect(() => {
    if (!organizationDefault || !isLanguageCode(organizationDefault)) return;
    
    const cookieLang = Cookies.get('language');
    const storageLang = localStorage.getItem('language');
    
    // Only apply organization default if user has no saved preference
    if (!(cookieLang && isLanguageCode(cookieLang)) && !(storageLang && isLanguageCode(storageLang))) {
      setLanguageState(organizationDefault);
      syncI18nLanguage(organizationDefault);
    }
  }, [organizationDefault]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);

    syncLanguageStorage(lang);
    syncI18nLanguage(lang);
    // Portal has no staff profile — language persists via cookie/localStorage only.
    // Customer-specific language persistence lives in CustomerAuthProvider.
  };

  // Update document language attribute when language changes
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, organizationDefault }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguageContext() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
}
